import { useEffect, useState } from "react"
import { API_BASE_URL } from "../config"
import { useNavigate } from "react-router-dom"
import { AppSidebar } from "@/components/app-sidebar"
import { SiteHeader } from "@/components/site-header"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card"
import { Field, FieldLabel, FieldGroup, FieldDescription } from "@/components/ui/field"
import { Input } from "@/components/ui/input"
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { logoutUser, getSession, getToken } from "@/lib/auth"
import {
  fetchAdminRequestQueue,
  fetchAdminRequestDetails,
  updateAdminRequestStatus,
  submitAdminRequestResolution,
  type AdminRequestQueueItem,
  type AdminRequestDetail
} from "@/lib/adminReviewApi"
import { EyeIcon, CheckCircleIcon, XCircleIcon, SearchIcon, AlertCircleIcon, FileTextIcon, UserIcon, BriefcaseIcon, XIcon } from "lucide-react"
import { ReportsDashboard } from "./ReportsDashboard"

function renderParsedDescription(raw: string) {
  try {
    // Attempt to extract JSON if it's embedded after "Datos dinamicos:\n"
    let jsonStr = raw
    const jsonMatch = raw.match(/Datos dinamicos:\s*(\{.*\})/)
    if (jsonMatch && jsonMatch[1]) {
      jsonStr = jsonMatch[1]
    }
    
    const parsed = JSON.parse(jsonStr)
    const descriptionPart = raw.replace(/Datos dinamicos:\s*\{.*\}/, '').trim()
    
    if (typeof parsed === "object" && parsed !== null) {
      return (
        <div className="space-y-4">
          {descriptionPart && (
            <div className="whitespace-pre-wrap">{descriptionPart}</div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            {Object.entries(parsed).map(([key, value]) => (
              <div key={key}>
                <span className="text-xs text-muted-foreground block capitalize">{key.replace(/_/g, ' ')}</span>
                <span className="font-medium">{String(value)}</span>
              </div>
            ))}
          </div>
        </div>
      )
    }
  } catch {
    // If not valid JSON, return as raw string
  }
  return <div className="whitespace-pre-wrap">{raw}</div>
}

export function AdminReviewDashboard() {
  const navigate = useNavigate()
  const session = getSession()

  const [queue, setQueue] = useState<AdminRequestQueueItem[]>([])
  const [statusFilter, setStatusFilter] = useState("all")
  const [cargandoQueue, setCargandoQueue] = useState(true)
  const [errorQueue, setErrorQueue] = useState("")
  
  const isReportsView = window.location.pathname.includes("reports")

  const [selectedRequestId, setSelectedRequestId] = useState<number | null>(null)
  const [requestDetail, setRequestDetail] = useState<AdminRequestDetail | null>(null)
  const [cargandoDetalle, setCargandoDetalle] = useState(false)
  const [errorDetalle, setErrorDetalle] = useState("")

  const [resolutionDecision, setResolutionDecision] = useState("")
  const [resolutionJustification, setResolutionJustification] = useState("")
  const [resolviendo, setResolviendo] = useState(false)
  
  const [actualizandoEstado, setActualizandoEstado] = useState(false)
  
  const [previewFile, setPreviewFile] = useState<{ url: string, name: string } | null>(null)

  const fullName = session?.fullName ?? "Administrador"
  const email = session?.email ?? "admin@inseso.gq"

  async function loadQueue() {
    try {
      setCargandoQueue(true)
      const token = getToken()
      if (!token) throw new Error("No autenticado.")
      
      const filter = statusFilter === "all" ? undefined : statusFilter
      const data = await fetchAdminRequestQueue(token, filter)
      setQueue(data)
    } catch (err) {
      setErrorQueue(err instanceof Error ? err.message : "Error al cargar la bandeja.")
    } finally {
      setCargandoQueue(false)
    }
  }

  useEffect(() => {
    loadQueue()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter])

  async function abrirDetalle(requestId: number) {
    setSelectedRequestId(requestId)
    setRequestDetail(null)
    setErrorDetalle("")
    setResolutionDecision("")
    setResolutionJustification("")
    
    try {
      setCargandoDetalle(true)
      const token = getToken()
      if (!token) throw new Error("No autenticado.")
      const data = await fetchAdminRequestDetails(token, requestId)
      setRequestDetail(data)
    } catch (err) {
      setErrorDetalle(err instanceof Error ? err.message : "Error al cargar el detalle.")
    } finally {
      setCargandoDetalle(false)
    }
  }

  function cerrarDetalle() {
    setSelectedRequestId(null)
    setRequestDetail(null)
  }

  async function cambiarEstado(nuevoEstado: string) {
    if (!selectedRequestId) return
    try {
      setActualizandoEstado(true)
      const token = getToken()
      if (!token) throw new Error("No autenticado")
      await updateAdminRequestStatus(token, selectedRequestId, nuevoEstado)
      
      // Update local state
      if (requestDetail) {
        setRequestDetail({ ...requestDetail, estado: nuevoEstado })
      }
      loadQueue()
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al actualizar estado")
    } finally {
      setActualizandoEstado(false)
    }
  }

  async function resolverSolicitud() {
    if (!selectedRequestId) return
    if (!resolutionDecision) {
      alert("Selecciona Aprobado o Denegado.")
      return
    }
    if (!resolutionJustification.trim()) {
      alert("La justificación es obligatoria.")
      return
    }

    try {
      setResolviendo(true)
      const token = getToken()
      if (!token) throw new Error("No autenticado")
      const data = await submitAdminRequestResolution(token, selectedRequestId, resolutionDecision, resolutionJustification)
      
      if (requestDetail) {
        setRequestDetail({ ...requestDetail, estado: data.estado })
      }
      loadQueue()
      alert("Resolución guardada exitosamente.")
      cerrarDetalle()
    } catch (err) {
      alert(err instanceof Error ? err.message : "Error al guardar resolución")
    } finally {
      setResolviendo(false)
    }
  }

  function cerrarSesion() {
    logoutUser()
    navigate("/login", { replace: true })
  }

  return (
    <SidebarProvider>
      <AppSidebar
        user={{
          name: fullName,
          email: email,
        }}
        role={session?.role}
        onLogout={cerrarSesion}
      />
      <SidebarInset>
        <SiteHeader title="Revisión Administrativa INSESO" />
        <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
          {isReportsView ? (
            <ReportsDashboard />
          ) : !selectedRequestId ? (
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Bandeja de Solicitudes</CardTitle>
                  <CardDescription>Visualiza y gestiona las solicitudes pendientes y revisadas.</CardDescription>
                </div>
                <div className="w-64">
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="Filtrar por estado" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      <SelectItem value="Enviado">Enviado</SelectItem>
                      <SelectItem value="En Revisión">En Revisión</SelectItem>
                      <SelectItem value="Verificado">Verificado</SelectItem>
                      <SelectItem value="Resuelto - Aprobado">Resuelto - Aprobado</SelectItem>
                      <SelectItem value="Resuelto - Denegado">Resuelto - Denegado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {errorQueue ? (
                  <p className="text-sm text-destructive">{errorQueue}</p>
                ) : cargandoQueue ? (
                  <p className="text-sm text-muted-foreground">Cargando bandeja...</p>
                ) : queue.length === 0 ? (
                  <div className="py-8 text-center text-muted-foreground">
                    No se encontraron solicitudes con este filtro.
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID</TableHead>
                        <TableHead>Afiliado</TableHead>
                        <TableHead>Prestación</TableHead>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Estado</TableHead>
                        <TableHead className="text-right">Acción</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {queue.map((req) => (
                        <TableRow key={req.id}>
                          <TableCell className="font-medium">{req.tracking_id}</TableCell>
                          <TableCell>
                            <div>{req.afiliado_nombre}</div>
                            <div className="text-xs text-muted-foreground">{req.afiliado_identificacion}</div>
                          </TableCell>
                          <TableCell>{req.beneficio}</TableCell>
                          <TableCell>{new Date(req.fecha_solicitud).toLocaleDateString("es-GQ")}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                              {req.estado}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button variant="outline" size="sm" onClick={() => abrirDetalle(req.id)}>
                              <EyeIcon className="size-4 mr-2" />
                              Revisar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <Button variant="outline" onClick={cerrarDetalle}>
                  ← Volver a Bandeja
                </Button>
                {requestDetail && (
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-muted-foreground">ID:</span> {requestDetail.tracking_id}
                    <span className="font-semibold text-muted-foreground ml-4">Estado Actual:</span>
                    <span className="inline-flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
                      {requestDetail.estado}
                    </span>
                  </div>
                )}
              </div>

              {errorDetalle ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-destructive">{errorDetalle}</p>
                  </CardContent>
                </Card>
              ) : cargandoDetalle || !requestDetail ? (
                <Card>
                  <CardContent className="pt-6">
                    <p className="text-sm text-muted-foreground">Cargando detalles de la solicitud...</p>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardHeader>
                    <CardTitle>{requestDetail.beneficio}</CardTitle>
                    <CardDescription>
                      Revisión exhaustiva de perfil, historial de empleo y cotizaciones.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <Tabs defaultValue="perfil" className="w-full">
                      <TabsList className="mb-4">
                        <TabsTrigger value="perfil" className="gap-2"><UserIcon className="size-4" /> Perfil y Empleo</TabsTrigger>
                        <TabsTrigger value="cotizaciones" className="gap-2"><BriefcaseIcon className="size-4" /> Cotizaciones</TabsTrigger>
                        <TabsTrigger value="documentos" className="gap-2"><FileTextIcon className="size-4" /> Documentos</TabsTrigger>
                        <TabsTrigger value="resolucion" className="gap-2"><CheckCircleIcon className="size-4" /> Resolución</TabsTrigger>
                      </TabsList>
                      
                      <TabsContent value="perfil" className="space-y-6">
                        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 bg-muted/20 p-4 rounded-lg">
                          <div><span className="text-xs text-muted-foreground block">Nombre completo</span><span className="font-medium">{requestDetail.afiliado.nombre}</span></div>
                          <div><span className="text-xs text-muted-foreground block">Identificación</span><span className="font-medium">{requestDetail.afiliado.identificacion}</span></div>
                          <div><span className="text-xs text-muted-foreground block">NSS</span><span className="font-medium">{requestDetail.afiliado.nss}</span></div>
                          <div><span className="text-xs text-muted-foreground block">Teléfono</span><span className="font-medium">{requestDetail.afiliado.telefono}</span></div>
                          <div className="md:col-span-2"><span className="text-xs text-muted-foreground block">Dirección</span><span className="font-medium">{requestDetail.afiliado.direccion}, {requestDetail.afiliado.ciudad}, {requestDetail.afiliado.provincia}</span></div>
                        </div>

                        <div>
                          <h3 className="font-semibold mb-3">Historial de Empleo</h3>
                          {requestDetail.empleos.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No se registran empleos.</p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Empresa</TableHead>
                                  <TableHead>Cargo</TableHead>
                                  <TableHead>Salario</TableHead>
                                  <TableHead>Inicio</TableHead>
                                  <TableHead>Fin</TableHead>
                                  <TableHead>Estado</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {requestDetail.empleos.map((emp, i) => (
                                  <TableRow key={i}>
                                    <TableCell className="font-medium">{emp.empresa}</TableCell>
                                    <TableCell>{emp.cargo}</TableCell>
                                    <TableCell>{emp.salario} XAF</TableCell>
                                    <TableCell>{emp.inicio ? new Date(emp.inicio).toLocaleDateString("es-GQ") : "-"}</TableCell>
                                    <TableCell>{emp.fin ? new Date(emp.fin).toLocaleDateString("es-GQ") : "-"}</TableCell>
                                    <TableCell>{emp.activo ? "Activo" : "Inactivo"}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="cotizaciones">
                         <div>
                          <h3 className="font-semibold mb-3">Historial de Cotizaciones</h3>
                          {requestDetail.cotizaciones.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No se registran cotizaciones.</p>
                          ) : (
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Fecha</TableHead>
                                  <TableHead>Empresa</TableHead>
                                  <TableHead>Aporte Empleado</TableHead>
                                  <TableHead>Aporte Empleador</TableHead>
                                  <TableHead className="text-right">Total</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {requestDetail.cotizaciones.map((cot, i) => (
                                  <TableRow key={i}>
                                    <TableCell>{new Date(cot.fecha).toLocaleDateString("es-GQ")}</TableCell>
                                    <TableCell>{cot.empresa}</TableCell>
                                    <TableCell>{cot.empleado} XAF</TableCell>
                                    <TableCell>{cot.empleador} XAF</TableCell>
                                    <TableCell className="text-right font-medium">{cot.monto_total} XAF</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="documentos" className="space-y-6">
                        <div>
                          <h3 className="font-semibold mb-2">Datos dinámicos / Descripción</h3>
                          <div className="bg-muted/20 p-4 rounded-lg text-sm">
                            {renderParsedDescription(requestDetail.descripcion)}
                          </div>
                        </div>

                        <div>
                          <h3 className="font-semibold mb-3">Archivos Adjuntos</h3>
                          {requestDetail.archivos.length === 0 ? (
                            <p className="text-sm text-muted-foreground">No se encontraron archivos.</p>
                          ) : (
                            <div className="grid gap-3 md:grid-cols-2">
                              {requestDetail.archivos.map(file => (
                                <div key={file.id} className="flex items-center justify-between p-3 border rounded-lg bg-card">
                                  <div className="flex items-center gap-3 overflow-hidden">
                                    <FileTextIcon className="size-8 text-primary shrink-0" />
                                    <div className="overflow-hidden">
                                      <p className="font-medium text-sm truncate" title={file.file_name}>{file.file_name}</p>
                                      <p className="text-xs text-muted-foreground">{Math.ceil(Number(file.file_size))} KB</p>
                                    </div>
                                  </div>
                                  <Button variant="secondary" size="sm" onClick={() => setPreviewFile({ url: `${API_BASE_URL}${file.file_url}`, name: file.file_name })}>
                                    Ver
                                  </Button>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </TabsContent>

                      <TabsContent value="resolucion" className="space-y-8">
                        {requestDetail.estado.startsWith("Resuelto") ? (
                          <div className="p-6 text-center border rounded-lg bg-muted/20">
                            <CheckCircleIcon className="size-12 text-primary mx-auto mb-4" />
                            <h3 className="text-xl font-bold">Solicitud ya resuelta</h3>
                            <p className="text-muted-foreground mt-2">Esta solicitud ya cuenta con un dictamen de resolución.</p>
                          </div>
                        ) : (
                          <>
                            <div>
                              <h3 className="font-semibold mb-4">Actualizar estado de revisión</h3>
                              <div className="flex items-center gap-3">
                                <Button 
                                  variant={requestDetail.estado === "En Revisión" ? "secondary" : "outline"} 
                                  onClick={() => cambiarEstado("En Revisión")}
                                  disabled={actualizandoEstado}
                                >
                                  Marcar En Revisión
                                </Button>
                                <Button 
                                  variant={requestDetail.estado === "Verificado" ? "secondary" : "outline"}
                                  onClick={() => cambiarEstado("Verificado")}
                                  disabled={actualizandoEstado}
                                >
                                  Marcar Verificado
                                </Button>
                              </div>
                            </div>

                            <hr />

                            <div>
                              <h3 className="font-semibold mb-4 text-lg">Emitir Resolución Final</h3>
                              <div className="max-w-2xl space-y-4">
                                <FieldGroup>
                                  <Field>
                                    <FieldLabel>Decisión (Obligatorio)</FieldLabel>
                                    <Select value={resolutionDecision} onValueChange={setResolutionDecision}>
                                      <SelectTrigger>
                                        <SelectValue placeholder="Selecciona decisión" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        <SelectItem value="Aprobado">Aprobado</SelectItem>
                                        <SelectItem value="Denegado">Denegado</SelectItem>
                                      </SelectContent>
                                    </Select>
                                  </Field>
                                  <Field>
                                    <FieldLabel>Justificación (Obligatorio)</FieldLabel>
                                    <FieldDescription>Proporciona el razonamiento detallado de la decisión según la normativa INSESO.</FieldDescription>
                                    <textarea
                                      className="min-h-32 w-full rounded-lg border border-input bg-transparent px-2.5 py-2 text-sm focus-visible:border-ring focus-visible:outline-none"
                                      value={resolutionJustification}
                                      onChange={(e) => setResolutionJustification(e.target.value)}
                                      placeholder="Justificación de la resolución..."
                                    />
                                  </Field>
                                </FieldGroup>
                                <Button 
                                  className="w-full" 
                                  size="lg" 
                                  onClick={resolverSolicitud}
                                  disabled={resolviendo || !resolutionDecision || !resolutionJustification.trim()}
                                >
                                  {resolviendo ? "Procesando..." : "Emitir Resolución y Finalizar"}
                                </Button>
                              </div>
                            </div>
                          </>
                        )}
                      </TabsContent>
                    </Tabs>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>
      </SidebarInset>
      
      {/* Document Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/80 backdrop-blur-sm p-4 md:p-8">
          <div className="w-full max-w-5xl bg-background rounded-xl overflow-hidden shadow-2xl flex flex-col h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b">
              <div className="font-semibold truncate pr-4 flex items-center gap-2">
                <FileTextIcon className="size-5 text-primary" />
                {previewFile.name}
              </div>
              <Button variant="ghost" size="icon" onClick={() => setPreviewFile(null)}>
                <XIcon className="size-5" />
              </Button>
            </div>
            <div className="flex-1 overflow-auto bg-muted/30 relative flex items-center justify-center">
              {previewFile.url.match(/\.(jpeg|jpg|gif|png)$/i) ? (
                <img src={previewFile.url} alt={previewFile.name} className="object-contain w-full h-full p-4" />
              ) : (
                <iframe 
                  src={previewFile.url} 
                  className="absolute inset-0 w-full h-full border-0" 
                  title={previewFile.name}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </SidebarProvider>
  )
}
